/**
 * 手機 PWA 返回鍵處理：
 * - 僅在「手機／平板 + PWA 模式」啟用
 * - 按一次返回鍵顯示提示
 * - 在短時間內再次按返回鍵 → 嘗試退出 PWA
 *
 * 這是從 TaskyVenture 專案簡化移植而來的版本。
 */

import { isTWAEnvironment, isPWAEnvironment } from './platformDetection';

interface BackButtonConfig {
  doubleClickInterval: number;
  showToast: boolean;
  exitMessage: string;
  confirmMessage: string;
}

class BackButtonHandler {
  private lastBackPressTime = 0;
  private config: BackButtonConfig;
  private isInitialized = false;
  private popstateListener: ((event: PopStateEvent) => void) | null = null;
  private toastTimeout: number | null = null;

  constructor(config?: Partial<BackButtonConfig>) {
    this.config = {
      doubleClickInterval: 2000,
      showToast: true,
      exitMessage: '再按一次退出應用',
      confirmMessage: '正在退出應用...',
      ...config,
    };
  }

  public init(): void {
    if (this.isInitialized) return;

    // 僅在手機／平板 + PWA 模式啟用；TWA 交給原生端處理
    const ua = navigator.userAgent.toLowerCase();
    const isMobileOrTablet = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(ua);

    if (!isMobileOrTablet) return;
    if (isTWAEnvironment()) return;
    if (!isPWAEnvironment()) return;

    this.setupHistoryState();
    this.setupBrowserBackButton();
    this.isInitialized = true;
  }

  private setupBrowserBackButton(): void {
    this.popstateListener = (event: PopStateEvent) => {
      // 阻止預設返回行為，改為雙擊退出邏輯
      event.preventDefault();
      this.handleBackButtonPress();

      // 重新 push 一個 state，避免真的往前一個頁面退
      window.history.pushState({ backButtonHandler: true }, document.title, window.location.href);
    };

    window.addEventListener('popstate', this.popstateListener);
  }

  private setupHistoryState(): void {
    // 確保 history 中至少有一筆可供返回的紀錄
    if (window.history.length <= 1) {
      window.history.pushState({ backButtonHandler: true }, document.title, window.location.href);
    }
  }

  private handleBackButtonPress(): void {
    const now = Date.now();
    const diff = now - this.lastBackPressTime;

    if (diff < this.config.doubleClickInterval && this.lastBackPressTime > 0) {
      // 雙擊：嘗試退出應用
      this.exitApplication();
    } else {
      // 第一次按：記錄時間並顯示提示
      this.lastBackPressTime = now;
      if (this.config.showToast) {
        this.showExitToast();
      }
    }
  }

  private exitApplication(): void {
    const isPWA = isPWAEnvironment();
    const isTWA = isTWAEnvironment();

    if (!isPWA && !isTWA) return;

    if (this.config.showToast) {
      this.showToast(this.config.confirmMessage, 1000);
    }

    setTimeout(() => {
      try {
        // 嘗試關閉視窗；在 PWA 中通常會使應用退出
        window.close();
      } catch {
        // 如果無法關閉，退回到首頁作為最後備援
        window.location.href = '/';
      }
    }, 500);
  }

  private showExitToast(): void {
    this.showToast(this.config.exitMessage, this.config.doubleClickInterval);
  }

  private showToast(message: string, duration: number = 3000): void {
    if (this.toastTimeout !== null) {
      window.clearTimeout(this.toastTimeout);
      this.toastTimeout = null;
    }

    const existing = document.getElementById('back-button-toast');
    if (existing) {
      existing.remove();
    }

    const toast = document.createElement('div');
    toast.id = 'back-button-toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.86);
      color: #fff;
      padding: 8px 18px;
      border-radius: 999px;
      font-size: 14px;
      font-weight: 500;
      z-index: 10000;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      backdrop-filter: blur(10px);
      max-width: 80%;
      text-align: center;
      white-space: nowrap;
    `;

    document.body.appendChild(toast);

    this.toastTimeout = window.setTimeout(() => {
      if (toast.parentNode) {
        toast.remove();
      }
      this.toastTimeout = null;
    }, duration);
  }

  public destroy(): void {
    if (this.popstateListener) {
      window.removeEventListener('popstate', this.popstateListener);
      this.popstateListener = null;
    }

    if (this.toastTimeout !== null) {
      window.clearTimeout(this.toastTimeout);
      this.toastTimeout = null;
    }

    const toast = document.getElementById('back-button-toast');
    if (toast) {
      toast.remove();
    }

    this.isInitialized = false;
  }
}

const backButtonHandler = new BackButtonHandler();

export const initBackButtonHandler = (config?: Partial<BackButtonConfig>) => {
  if (config) {
    const handler = new BackButtonHandler(config);
    handler.init();
    return handler;
  }
  backButtonHandler.init();
  return backButtonHandler;
};

export const destroyBackButtonHandler = () => {
  backButtonHandler.destroy();
};

export default BackButtonHandler;

